import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "WastewaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Water & Liquid Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Mesh & Screens for Wastewater Filtration",
      lead: "Engineered heavy-duty woven wire mesh, sintered media, and continuous-slot wedge wire screens designed for municipal and industrial wastewater separation, primary effluent screening, and sludge dewatering operations.",
      secondaryCta: {
        label: "Water & Liquid Filtration",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Industrial dewatering and rotary drum screening equipment fitted with stainless steel filter mesh",
      },
      cardNote: "Rigid all-metal construction & anti-clogging profiles",
      cardLink: {
        label: "Request Technical Review",
        to: "/ContactPage",
      },
      specs: [
        { label: "Aperture Range", value: "20 µm – 5,000 µm" },
        { label: "Alloy Metallurgy", value: "AISI 304 / 316L" },
        { label: "Fabrication", value: "Welded / Sintered / Continuous" },
        { label: "Cleanability", value: "Automated Backwash / Spray" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "Robust Solid-Liquid Separation for High-Turbidity Wastewater Streams",
      paragraphs: [
        "Wastewater treatment facilities and industrial effluent processing circuits operate under relentless hydraulic throughput and variable solids loading. Coarse fibrous debris, abrasive mineral grit, and biological flocs must be captured continuously without blinding the screening interface or causing unmanageable hydraulic head loss.",
        "To satisfy these operational demands, MutualFilter provides precision-engineered metallic separation media including continuous-slot wedge wire screens, robust plain and twilled Dutch weave cloth, and diffusion-bonded multilayer laminates. The geometric profiles are selected to minimize two-point particle wedging and ensure instantaneous cake release during automated mechanical scraping or high-velocity spray washing.",
        "Fabricated from passivated AISI 304 and 316L stainless steel, every screen component is designed to endure aggressive industrial chemistries, chloride exposure, fluctuating effluent pH, and routine CIP sanitation cycles while maintaining dimensional accuracy and structural rigidity.",
      ],
      keyPoints: [
        "V-wire continuous-slot and calibrated woven profiles prevent particulate blinding and provide dependable cake release",
        "High open-area geometries sustain rapid hydraulic throughput with low clean pressure drop across variable solids loadings",
        "Passivated AISI 304 and 316L stainless steel alloys withstand industrial chemical washdowns and corrosive effluents",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Industrial wastewater screening equipment with stainless steel screen elements",
      },
    },
    specTable: {
      heading: "Screen Media Configurations for Wastewater Operations",
      intro: "Representative metallic filter media configurations tailored for municipal intake, industrial effluent processing, and mechanical dewatering equipment.",
      columns: [
        { key: "stage", label: "Treatment Stage", align: "left" },
        { key: "media", label: "Media Construction", align: "left" },
        { key: "rating", label: "Nominal Aperture", align: "right" },
        { key: "alloys", label: "Standard Alloys", align: "right" },
        { key: "mechanism", label: "Cleaning Method", align: "right" },
      ],
      rows: [
        {
          stage: "Primary Intake & Coarse Screening",
          media: "Continuous-slot wedge wire panel / cylinder",
          rating: "500 µm – 5,000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Mechanical scraper / rake",
        },
        {
          stage: "Rotary Drum Fine Screening",
          media: "V-wire screen tube / curved panel",
          rating: "100 µm – 1,000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "High-pressure spray bar",
        },
        {
          stage: "Sludge Dewatering & Pressing",
          media: "Heavy square weave wire cloth",
          rating: "150 µm – 800 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Belt wash / scraper blade",
        },
        {
          stage: "Tertiary Clarification & Polishing",
          media: "Plain Dutch weave / sintered mesh tube",
          rating: "20 µm – 100 µm",
          alloys: "AISI 316L",
          mechanism: "Automated backwash pulse",
        },
      ],
      caption: "All aperture ratings and mechanical dimensions are engineered to order according to process flow rates and effluent characteristics.",
    },
    features: {
      heading: "Key Operational & Manufacturing Advantages",
      intro: "Our stainless steel wastewater filter media combine structural endurance, non-blinding geometry, and metallurgical integrity across continuous separation duty.",
      items: [
        {
          title: "Anti-Blinding V-Wire Geometry",
          text: "Continuous-slot profile wires feature widening relief angles that ensure near-size solids touch only two contact points before dislodging, minimizing slot blinding and blinding frequency.",
        },
        {
          title: "Rigid Structural Reinforcements",
          text: "Heavy support rods, outer protective shrouds, and heavy-gauge perforated backer cores provide resistance against hydraulic surges and high mechanical scraping loads.",
        },
        {
          title: "Corrosion & Chemistry Resistance",
          text: "Passivated AISI 304 and molybdenum-bearing 316L grades endure varied effluent pH levels, industrial organic compounds, and caustic CIP cleaning chemicals without pitting.",
        },
        {
          title: "Automated Backwash Compatibility",
          text: "Smooth metallic surfaces and rigid weld seams facilitate rapid backflush cycles, spray nozzle washdowns, or mechanical blade clearance without media displacement.",
        },
      ],
    },
    related: {
      heading: "Related Water Filtration Applications & Products",
      intro: "Explore complementary liquid separation applications and precision metallic filter media manufactured for demanding water treatment systems.",
      cards: [
        {
          title: "Water & Liquid Filtration",
          text: "Comprehensive metallic filtration solutions for municipal water, process cooling circuits, liquid straining, and industrial effluent treatment.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Parent Application",
          linkLabel: "View Application Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Water and liquid filtration overview showing stainless steel separation media",
          },
        },
        {
          title: "Industrial Water Filtration",
          text: "Process water intake, cooling tower loops, and pump protection screens engineered for minimal clean pressure drop and high throughput.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Industrial Water",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Industrial stainless steel water filtration equipment and precision screen installation",
          },
        },
        {
          title: "Liquid Straining Filtration",
          text: "High-differential-pressure metallic strainer baskets, cylinders, and Dutch weave elements for chemical slurries and process fluids.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Sibling Application",
          linkLabel: "Explore Liquid Straining",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Clean macro photograph of Dutch weave stainless steel wire mesh for liquid filtration",
          },
        },
        {
          title: "Wedge Wire Screens",
          text: "Continuous-slot V-wire flat panels, curved sieve bends, and cylindrical screen tubes engineered for non-clogging solid-liquid separation.",
          to: "/WedgeWireScreenPage",
          badge: "Recommended Product",
          linkLabel: "Explore Wedge Wire",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Continuous slot stainless steel wedge wire screen tube for wastewater and water filtration",
          },
        },
        {
          title: "Filter Components",
          text: "Custom fabricated stainless steel filter tubes, cylindrical elements, and strainer baskets for automated backwash separation housings.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Stainless steel welded and sintered filter tubes and cylinder screen components",
          },
        },
      ],
    },
    cta: {
      id: "wastewater-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Wastewater Screen or Mesh Specification",
      lead: "Provide your target slot width or aperture rating, overall screen dimensions, alloy preference, and mechanical mounting requirements for an engineering review and quotation.",
      secondaryLabel: "Water & Liquid Filtration",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "WastewaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "水与液体过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "工业废水与污水处理不锈钢筛网滤材",
      lead: "专为市政污水固液分离、工业废水原水初筛、高浊度悬浮物截留与污泥脱水系统设计制造的重载不锈钢编织丝网与连续缝隙楔形网构件。",
      secondaryCta: {
        label: "水与液体过滤总览",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "配置不锈钢滤网的工业脱水与滚筒筛分设备",
      },
      cardNote: "全金属刚性结构与防堵孔型面设计",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "孔径缝隙范围", value: "20 µm – 5,000 µm" },
        { label: "常用材质牌号", value: "AISI 304 / 316L" },
        { label: "工艺结构形式", value: "焊接 / 烧结 / 连续绕丝" },
        { label: "清洗再生方式", value: "自动反洗 / 喷淋冲洗" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "针对高浊度污水与工业废液的高可靠固液分离",
      paragraphs: [
        "污水处理设施与工业废液处理回路常年处于大通量水力负荷与悬浮固含波动工况下。纤维杂质、无机泥沙颗粒以及生物絮体必须被持续高效截留，同时必须避免滤面堵塞和过大的水头损失。",
        "为满足连续处理要求，MutualFilter 提供工程级全金属分离介质，包括连续缝隙楔形丝筛网、坚固的平纹与斜纹荷兰编织滤布以及扩散焊接多层烧结复合板。其独特的截面型面设计有效避免颗粒卡夹，并在机械刮刷或高压反洗喷淋时实现迅速脱饼脱渣。",
        "产品选用酸洗钝化处理的 AISI 304 与 316L 不锈钢精工制造，在抵御工业化学药剂腐蚀、氯离子侵蚀与多变 pH 废液的同时，长期维持稳定的几何尺寸精度与抗水力冲击刚性。",
      ],
      keyPoints: [
        "连续V型楔形截面与高精度编织微孔两点接触防堵孔设计，反洗脱饼再生迅速",
        "高开孔率流体截面在固含波动工况下仍能保持充沛流通能力与极低初始水头损失",
        "酸洗钝化 AISI 304 与 316L 不锈钢稳定耐受氯离子、酸碱清洗液与工业污水侵蚀",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "工业污水筛分设备与不锈钢筛网组件",
      },
    },
    specTable: {
      heading: "污水与废水处理常用滤材配置参数",
      intro: "以下列出适用于原水取水、转鼓微滤、污泥脱水以及深度澄清工况的典型金属过滤介质配置。",
      columns: [
        { key: "stage", label: "工艺处理环节", align: "left" },
        { key: "media", label: "核心构型与介质", align: "left" },
        { key: "rating", label: "名义截留缝隙/孔径", align: "right" },
        { key: "alloys", label: "推荐合金材质", align: "right" },
        { key: "mechanism", label: "清洗再生方式", align: "right" },
      ],
      rows: [
        {
          stage: "原水进水与粗粒拦截",
          media: "连续缝隙楔形丝平板 / 弧形筛",
          rating: "500 µm – 5,000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "机械刮板 / 耙齿刮刷",
        },
        {
          stage: "转鼓微滤与精细拦渣",
          media: "V型绕丝筛管 / 弧形筛板",
          rating: "100 µm – 1,000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "高压反冲洗喷淋管",
        },
        {
          stage: "污泥压榨与固液脱水",
          media: "重载方孔不锈钢编织网",
          rating: "150 µm – 800 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "高压水冲洗 / 刮泥刀",
        },
        {
          stage: "深度澄清与微细颗粒截留",
          media: "平纹荷兰编织网 / 多层烧结管",
          rating: "20 µm – 100 µm",
          alloys: "AISI 316L",
          mechanism: "自动化脉冲反冲洗",
        },
      ],
      caption: "各项缝隙精度、机械尺寸及外框连接形式均可根据实际工艺通量与水质指标定制加工。",
    },
    features: {
      heading: "关键运行与结构技术优势",
      intro: "我们的不锈钢废水过滤介质兼具结构抗压刚性、防堵塞型面几何与可靠的耐腐蚀冶金性能，适应长周期连续作业。",
      items: [
        {
          title: "V型防堵塞截面型线",
          text: "连续缝隙楔形丝截面具有向外扩展的侧边斜度，微粒仅与迎水面两点接触即被截留或顺利通过，显著降低嵌塞堵孔风险。",
        },
        {
          title: "刚性承载与加固骨架",
          text: "配置厚壁加强筋条、冲孔外护套与内置加固管，在强水力水锤、频繁反冲洗及机械刮刷压力下保持不变形。",
        },
        {
          title: "优异耐腐蚀与抗化学侵蚀",
          text: "酸洗钝化 AISI 304 与含钼 316L 材质有效抵抗污水中的有机酸、硫化物、氯离子及常规在线 CIP 碱性清洗液。",
        },
        {
          title: "高效反冲洗与喷淋再生",
          text: "平滑的金属表面与紧密焊缝结构便于高压喷嘴冲刷或反向水流脉冲快速剥离滤饼，迅速恢复初始通水能力。",
        },
      ],
    },
    related: {
      heading: "相关水处理应用与推荐产品",
      intro: "浏览 MutualFilter 为工业与市政水处理行业打造的互补应用方案及精密金属过滤介质。",
      cards: [
        {
          title: "水与液体过滤",
          text: "涵盖工艺冷却水、原水取水拦截、液体精细筛滤及工业废液分离的全金属介质综合方案。",
          to: "/WaterLiquidFiltrationPage",
          badge: "父级应用分类",
          linkLabel: "查看应用总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "水与液体过滤总览及不锈钢过滤材料",
          },
        },
        {
          title: "工业水过滤",
          text: "工业循环冷却水、工艺给水及水泵吸入口保护滤网，具有超低初始流动压降与大通量特征。",
          to: "/IndustrialWaterFiltrationPage",
          badge: "同级应用",
          linkLabel: "探索工业水过滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "工业不锈钢水过滤设备与精密反冲洗滤网组件安装展示",
          },
        },
        {
          title: "液体过滤与筛滤",
          text: "高耐压差不锈钢编织丝网、滤篮与荷兰编织滤芯，用于流体澄清及高固含浆料拦截。",
          to: "/LiquidFiltrationStrainingPage",
          badge: "同级应用",
          linkLabel: "探索液体筛滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "用于液体过滤的不锈钢密纹编织金属丝网微观截留结构",
          },
        },
        {
          title: "楔形丝筛网",
          text: "抗堵塞连续缝隙 V 型绕丝平板筛、弧形筛及圆筒筛管，专为高负荷固液分离打造。",
          to: "/WedgeWireScreenPage",
          badge: "推荐产品",
          linkLabel: "探索楔形丝网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "水过滤用连续缝隙不锈钢楔形丝筛管",
          },
        },
        {
          title: "过滤组件",
          text: "定制焊接与烧结圆筒滤筒、自清洗过滤器内网与滤篮组件，匹配各类工业自清洗设备。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "不锈钢焊接与烧结圆筒滤筒滤网构件",
          },
        },
      ],
    },
    cta: {
      id: "wastewater-quote",
      eyebrow: "定制方案咨询",
      heading: "获取废水过滤筛网与金属滤材技术报价",
      lead: "请提供您的目标缝隙/孔径、外形尺寸、材质要求及设备安装形式，我们的工程团队将为您提供针对性的选型与报价方案。",
      secondaryLabel: "返回水与液体过滤",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "WastewaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtración de Agua y Líquidos",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mallas y Cribas de Acero Inoxidable para Filtración de Aguas Residuales",
      lead: "Mallas de alambre tejidas de alta resistencia, medios sinterizados y cribas de alambre en cuña de ranura continua diseñadas para separación de efluentes, desbaste primario y deshidratación de lodos.",
      secondaryCta: {
        label: "Filtración de Agua y Líquidos",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Equipo industrial de deshidratación y cribado de tambor rotativo con malla filtrante de acero inoxidable",
      },
      cardNote: "Construcción totalmente metálica y perfiles anticolmatantes",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de Abertura", value: "20 µm – 5.000 µm" },
        { label: "Grados de Aleación", value: "AISI 304 / 316L" },
        { label: "Construcción", value: "Soldado / Sinterizado / Continuo" },
        { label: "Limpieza", value: "Retrolavado Automático / Aspersión" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Separación Sólido-Líquido Robusta para Efluentes de Alta Turbidez",
      paragraphs: [
        "Las plantas depuradoras y los circuitos industriales de aguas residuales operan bajo caudales hidráulicos continuos y cargas variables de sólidos. Residuos fibrosos, arenas abrasivas y flóculos biológicos deben ser interceptados sin colmatar la superficie filtrante ni generar pérdidas de carga excesivas.",
        "Para responder a estas exigencias, MutualFilter fabrica medios de separación metálicos que incluyen cribas de alambre en cuña de ranura continua, telas Dutch reforzadas y laminados sinterizados multicapa. Sus perfiles minimizan el atrapamiento en dos puntos y facilitan la descarga del pastel durante el rascado mecánico o el lavado por aspersión a presión.",
        "Fabricados en acero inoxidable pasivado AISI 304 y 316L, los elementos resisten efluentes agresivos, cloruros y limpiezas CIP, conservando rigidez mecánica y precisión dimensional a largo plazo.",
      ],
      keyPoints: [
        "Perfiles de alambre en V y tejidos calibrados que previenen el cegamiento y facilitan el desprendimiento del pastel",
        "Geometrías con gran área abierta que mantienen un flujo hidráulico elevado con mínima pérdida de carga",
        "Aceros inoxidables AISI 304 y 316L pasivados que resisten ataques químicos y efluentes corrosivos",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Equipo industrial de aguas residuales con mallas de acero inoxidable",
      },
    },
    specTable: {
      heading: "Configuraciones de Mallas para Tratamiento de Aguas Residuales",
      intro: "Medios filtrantes metálicos representativos para captación primaria, tamices rotativos y deshidratación mecánica de lodos.",
      columns: [
        { key: "stage", label: "Etapa de Tratamiento", align: "left" },
        { key: "media", label: "Tipo de Medio Filtrante", align: "left" },
        { key: "rating", label: "Abertura / Ranura Nominal", align: "right" },
        { key: "alloys", label: "Aleaciones Típicas", align: "right" },
        { key: "mechanism", label: "Método de Limpieza", align: "right" },
      ],
      rows: [
        {
          stage: "Desbaste Grueso y Captación Primaria",
          media: "Panel plano / cilindro de alambre en cuña",
          rating: "500 µm – 5.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Rascador mecánico / rastrillo",
        },
        {
          stage: "Tamizado Fino en Tambor Rotativo",
          media: "Tubo de criba en V / panel curvado",
          rating: "100 µm – 1.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Barra de aspersión a presión",
        },
        {
          stage: "Deshidratación de Lodos y Prensado",
          media: "Tela tejida de malla cuadrada pesada",
          rating: "150 µm – 800 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Lavado de banda / rasqueta",
        },
        {
          stage: "Clarificación Terciaria y Afinamiento",
          media: "Tejido Dutch liso / tubo sinterizado",
          rating: "20 µm – 100 µm",
          alloys: "AISI 316L",
          mechanism: "Impulso de retrolavado automático",
        },
      ],
      caption: "Todas las dimensiones y ranuras se adaptan a medida según caudales y características del efluente.",
    },
    features: {
      heading: "Ventajas Operativas y Constructivas",
      intro: "Nuestros medios de filtración de aguas residuales combinan rigidez estructural, perfiles anticolmatantes y resistencia a la corrosión.",
      items: [
        {
          title: "Geometría Anticolmatante en V",
          text: "Las ranuras de perfil continuo cuentan con apertura divergente que asegura solo dos puntos de contacto con las partículas, reduciendo el riesgo de bloqueo.",
        },
        {
          title: "Refuerzos Estructurales Rígidos",
          text: "Barras de soporte pesadas y tubos perforados interiores que soportan sobrepresiones hidráulicas y esfuerzos de rascado mecánico continuo.",
        },
        {
          title: "Resistencia a la Corrosión",
          text: "Aceros AISI 304 y 316L pasivados que soportan oscilaciones de pH, compuestos orgánicos y productos alcalinos de limpieza CIP.",
        },
        {
          title: "Compatibilidad con Retrolavado",
          text: "Superficies lisas que permiten retrolavados rápidos y limpiezas mediante aspersores sin desplazar ni dañar el medio filtrante.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones y Productos Relacionados",
      intro: "Explore soluciones complementarias en filtración de agua y medios metálicos de precisión diseñados por MutualFilter.",
      cards: [
        {
          title: "Filtración de Agua y Líquidos",
          text: "Soluciones completas para tratamiento de agua de proceso, refrigeración y separación de efluentes industriales.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Visión general de filtración de agua y medios metálicos",
          },
        },
        {
          title: "Filtración de Agua Industrial",
          text: "Mallas para tomas de agua, torres de refrigeración y protección de bombas con mínima pérdida de carga inicial.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Agua Industrial",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Instalación de filtración de agua industrial con mallas de acero inoxidable",
          },
        },
        {
          title: "Filtración y Colado de Líquidos",
          text: "Cestas coladoras y elementos tubulares en tejido Dutch diseñados para retención de partículas en suspensiones líquidas.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Colado de Líquidos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Fotografía de tejido Dutch de acero inoxidable para filtración de líquidos",
          },
        },
        {
          title: "Cribas de Alambre en Cuña",
          text: "Cribas planas, curvas y cilindros de ranura continua anticolmatantes para separación sólido-líquido intensiva.",
          to: "/WedgeWireScreenPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Alambre en Cuña",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Tubo de criba de alambre en cuña para aguas residuales y filtración de agua",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Tubos soldados, cilindros sinterizados y cestas filtrantes fabricados a medida para carcasas de retrolavado automático.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos y cilindros filtrantes de acero inoxidable soldados y sinterizados",
          },
        },
      ],
    },
    cta: {
      id: "wastewater-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Mallas de Tratamiento de Aguas Residuales",
      lead: "Indique la ranura o abertura requerida, dimensiones totales, aleación y tipo de fijación mecánica para una revisión técnica y cotización.",
      secondaryLabel: "Volver a Filtración de Agua",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "WastewaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Wasser- & Flüssigkeitsfiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahlsiebe & Filtermedien für die Abwasserfiltration",
      lead: "Hochbelastbare Edelstahl-Drahtgewebe, gesinterte Medien und Spaltsiebe mit kontinuierlichem Schlitz für kommunale und industrielle Abwassertrennung, Primärrechen und Schlammentwässerung.",
      secondaryCta: {
        label: "Wasser- & Flüssigkeitsfiltration",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Industrielle Entwässerungs- und Trommelsiebanlage mit Edelstahl-Filtergewebe",
      },
      cardNote: "Ganzmetall-Stabilität & verstopfungsfreie Profile",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Spaltweitenbereich", value: "20 µm – 5.000 µm" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
        { label: "Konstruktion", value: "Geschweißt / Gesintert / Spaltsieb" },
        { label: "Reinigbarkeit", value: "Automatische Rückspülung / Spülung" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Robuste Fest-Flüssig-Trennung für hochtrübe Abwasserströme",
      paragraphs: [
        "Abwasserreinigungsanlagen und industrielle Aufbereitungskreisläufe unterliegen kontinuierlichen hydraulischen Durchsätzen und schwankenden Feststofffrachten. Faserstoffe, mineralische Sande und Schlammflocken müssen zuverlässig abgeschieden werden, ohne das Siebmedium zu verstopfen.",
        "Hierfür liefert MutualFilter präzise Ganzmetall-Trennmedien wie Spaltsiebe mit kontinuierlichem V-Draht-Schlitz, robuste Tressengewebe und diffusionsgebundene Sinterlaminate. Die Querschnittsgeometrie verhindert Zweipunkt-Verkeilungen und ermöglicht einen schnellen Kuchenabwurf bei automatischer Schabung oder Hochdruckspülung.",
        "Gefertigt aus passiviertem Edelstahl AISI 304 und 316L halten die Siebelemente industriellen Chemikalien, Chloriden und CIP-Spülungen stand, während Formstabilität und Trennschärfe über lange Zeiträume gewahrt bleiben.",
      ],
      keyPoints: [
        "Verstopfungsfreie V-Draht- und Gewebeprofile verhindern Verkeilungen und erleichtern das Lösen von Feststoffkuchen",
        "Hohe freie Siebflächen sichern stabilen Durchsatz bei minimalem Druckverlust auch bei Spitzenbelastungen",
        "Passivierte Edelstähle AISI 304 und 316L widerstehen chemischen Spülungen und korrosiven Abwässern",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Abwassersiebanlage mit Siebkörben und Spaltsieben aus Edelstahl",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für die Abwasseraufbereitung",
      intro: "Repräsentative Siebmedien für Wasserzulauf, Trommelsiebe, Schlammentwässerung und tertiäre Klärstufen.",
      columns: [
        { key: "stage", label: "Verfahrensstufe", align: "left" },
        { key: "media", label: "Filtermedium-Aufbau", align: "left" },
        { key: "rating", label: "Nennspaltweite / Masche", align: "right" },
        { key: "alloys", label: "Standard-Werkstoffe", align: "right" },
        { key: "mechanism", label: "Reinigungsmethode", align: "right" },
      ],
      rows: [
        {
          stage: "Zulaufrechen & Grobabscheidung",
          media: "Spaltsieb-Platte / Spaltzylinder",
          rating: "500 µm – 5.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Mechanischer Abstreifer / Rechen",
        },
        {
          stage: "Feinsiebung im Trommelsieb",
          media: "V-Draht-Spaltsiebrohr / Bogensieb",
          rating: "100 µm – 1.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Hochdruck-Düsensprührohr",
        },
        {
          stage: "Schlammentwässerung & Pressen",
          media: "Schweres Quadratmaschen-Gewebe",
          rating: "150 µm – 800 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Bandspülung / Schaberklinge",
        },
        {
          stage: "Tertiäre Polierung & Nachklärung",
          media: "Glatte Tresse / Sintergeweberohr",
          rating: "20 µm – 100 µm",
          alloys: "AISI 316L",
          mechanism: "Automatischer Rückspülpuls",
        },
      ],
      caption: "Alle Abmessungen und Spaltweiten werden auftragsbezogen an Durchflussraten und Abwasserparameter angepasst.",
    },
    features: {
      heading: "Betriebliche & Konstruktive Vorteile",
      intro: "Unsere Edelstahl-Abwassersiebe verbinden mechanische Dauerfestigkeit, verstopfungsfreie Geometrie und verlässliche Korrosionsbeständigkeit.",
      items: [
        {
          title: "Verstopfungsfreies V-Draht-Profil",
          text: "Erweiternde Schlitzöffnungen gewährleisten, dass Partikel nur an zwei Punkten anliegen und danach frei austreten, was Blockaden wirksam verhindert.",
        },
        {
          title: "Formstabile Verstärkungen",
          text: "Starke Stäbe und Lochblech-Stützkörper nehmen hydraulische Druckstöße und kontinuierliche Schaberbelastungen ohne Durchbiegung auf.",
        },
        {
          title: "Korrosions- & Chemikalienfestigkeit",
          text: "Passivierte Güten AISI 304 und molybdänhaltiges 316L widerstehen schwankenden pH-Werten, organischen Stoffen und alkalischen CIP-Reinigern.",
        },
        {
          title: "Eignung für Rückspülung",
          text: "Glatte Metallflächen ermöglichen schnelle Rückspülzyklen und Sprühreinigungen ohne Medienabrieb oder Gefügeverschiebung.",
        },
      ],
    },
    related: {
      heading: "Verwandte Wasserfiltrations-Anwendungen & Produkte",
      intro: "Entdecken Sie weitere industrielle Wasseranwendungen und präzise Metallfiltermedien aus der Fertigung von MutualFilter.",
      cards: [
        {
          title: "Wasser- & Flüssigkeitsfiltration",
          text: "Ganzheitliche metallische Trennlösungen für Prozesswasser, Kühlsysteme und industrielle Abwasserreinigung.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Wasser- und Flüssigkeitsfiltration Übersicht mit Edelstahl-Filtermedien",
          },
        },
        {
          title: "Industrielle Wasserfiltration",
          text: "Sieblösungen für Kühlkreisläufe, Entnahmesiebe und Pumpenschutz mit minimalem Anfangsdruckverlust.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Industriewasser ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Industrielle Edelstahl-Wasserfiltrationsausrüstung",
          },
        },
        {
          title: "Flüssigkeitsfiltration & Siebung",
          text: "Druckfeste Siebkörbe, Zylinder und Tressengewebe für anspruchsvolle Flüssigkeitsklärung und Schlammtrennung.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Flüssigkeitssiebung ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Edelstahl-Tressengewebe für die Flüssigkeitsfiltration",
          },
        },
        {
          title: "Spaltsiebe",
          text: "Verstopfungsfreie V-Draht-Flachpaneele, Bogensiebe und Zylinderrohre für intensive Fest-Flüssig-Trennung.",
          to: "/WedgeWireScreenPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Spaltsiebe entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Spaltsiebrohr aus Edelstahl für Wasser- und Abwasserfiltration",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Geschweißte und gesinterte Filterrohre, Zylinder und Siebkörbe für automatische Rückspülfiltergehäuse.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Filterkomponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Geschweißte und gesinterte Filterrohre aus Edelstahl",
          },
        },
      ],
    },
    cta: {
      id: "wastewater-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für Abwassersiebe & Filtermedien anfordern",
      lead: "Nennen Sie Spaltweite bzw. Maschenweite, Außenabmessungen, Werkstoff und Anschlussart für eine detaillierte technische Prüfung.",
      secondaryLabel: "Zurück zu Wasserfiltration",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "WastewaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtration d'Eau et de Liquides",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tamis et Grilles en Acier Inoxydable pour la Filtration des Eaux Usées",
      lead: "Toiles métalliques tissées robustes, médias frittés et grilles à fil profilé à fente continue pour le traitement des eaux résiduaires, le dégrillage primaire et la déshydratation des boues.",
      secondaryCta: {
        label: "Filtration d'Eau et de Liquides",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Équipement industriel de déshydratation et tamis à tambour rotatif équipé de toile filtrante en inox",
      },
      cardNote: "Conception tout métal et profils anticolmatants",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage d'Ouverture", value: "20 µm – 5 000 µm" },
        { label: "Nuances d'Alliages", value: "AISI 304 / 316L" },
        { label: "Fabrication", value: "Soudé / Fritté / Fil Profilé" },
        { label: "Nettoyabilité", value: "Rétrolavage Automatique / Pulvérisation" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Séparation Solide-Liquide Robuste pour Effluents à Forte Turbidité",
      paragraphs: [
        "Les stations d'épuration et circuits industriels d'effluents sont soumis à des débits hydrauliques intenses et à des charges fluctuantes en matières en suspension. Les matières fibreuses, sables minéraux et flocs doivent être interceptés en continu sans colmater le média filtrant.",
        "MutualFilter conçoit des médias métalliques de séparation comprenant des grilles à fil profilé à fente continue, des toiles à tissage hollandais renforcées et des stratifiés frittés multicouches. Leurs profils évitent l'encastrement des particules et favorisent le décolmatage rapide lors du raclage mécanique ou des rampes de lavage sous pression.",
        "Réalisés en acier inoxydable passivé AISI 304 et 316L, les éléments filtrants résistent aux attaques chimiques, aux chlorures et aux cycles NEP tout en conservant une rigidité mécanique durable.",
      ],
      keyPoints: [
        "Profils de fil en V et toiles calibrées évitant le colmatage et facilitant le détachement du gâteau de filtration",
        "Grandes surfaces ouvertes assurant un débit élevé avec une perte de charge minime lors des pics de charge",
        "Nuances passivées AISI 304 et 316L résistant aux effluents corrosifs et aux lavages chimiques",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Équipement industriel d'eaux usées avec tamis en acier inoxydable",
      },
    },
    specTable: {
      heading: "Configurations de Tamis pour le Traitement des Eaux Usées",
      intro: "Médias filtrants métalliques types pour dégrillage primaire, tamis à tambour et essorage mécanique des boues.",
      columns: [
        { key: "stage", label: "Étape de Procédé", align: "left" },
        { key: "media", label: "Type de Média Filtrant", align: "left" },
        { key: "rating", label: "Fente / Ouverture Nominale", align: "right" },
        { key: "alloys", label: "Alliages Standards", align: "right" },
        { key: "mechanism", label: "Mode de Nettoyage", align: "right" },
      ],
      rows: [
        {
          stage: "Dégrillage Primaire & Prise d'Eau",
          media: "Panneau plan / cylindre à fil profilé",
          rating: "500 µm – 5 000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Racleur mécanique / herse",
        },
        {
          stage: "Tamisage Fin sur Tambour Rotatif",
          media: "Tube à fil profilé en V / crible courbe",
          rating: "100 µm – 1 000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Rampe de pulvérisation sous pression",
        },
        {
          stage: "Déshydratation des Boues & Pressage",
          media: "Toile tissée à maille carrée lourde",
          rating: "150 µm – 800 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Lavage de bande / lame racleuse",
        },
        {
          stage: "Clarification Tertiaire & Polissage",
          media: "Tissage hollandais uni / tube fritté",
          rating: "20 µm – 100 µm",
          alloys: "AISI 316L",
          mechanism: "Pulsion de rétrolavage automatique",
        },
      ],
      caption: "Dimensions et seuils de fentes réalisés sur mesure selon les débits et les charges polluantes.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos toiles et grilles d'eaux usées combinent résistance mécanique, profils anticolmatants et intégrité métallurgique.",
      items: [
        {
          title: "Profil Anticolmatant en Fil V",
          text: "L'ouverture divergente de la fente garantit que les particules n'ont que deux points de contact, éliminant le risque d'obstruction permanente.",
        },
        {
          title: "Armatures Structurelles Rigides",
          text: "Tiges de support épaisses et tubes perforés de soutien résistant aux coups de bélier et aux sollicitations du raclage continu.",
        },
        {
          title: "Résistance Chimique et Corrosive",
          text: "Inox AISI 304 et 316L passivés supportant les variations de pH, les composés organiques et les détergents caustiques NEP.",
        },
        {
          title: "Aptitude au Contre-Lavage",
          text: "Surfaces métalliques lisses permettant des cycles de rétrolavage efficaces et des lavages par buses sans déformation de la toile.",
        },
      ],
    },
    related: {
      heading: "Applications et Produits Associés",
      intro: "Découvrez les solutions complémentaires de filtration d'eau et les médias métalliques conçus par MutualFilter.",
      cards: [
        {
          title: "Filtration d'Eau et de Liquides",
          text: "Solutions de séparation métallique pour eaux industrielles, circuits de refroidissement et traitement des effluents.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Présentation générale de la filtration d'eau avec toiles métalliques",
          },
        },
        {
          title: "Filtration d'Eau Industrielle",
          text: "Tamis pour prises d'eau, circuits de refroidissement et protection des pompes avec faible perte de charge.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Application Associée",
          linkLabel: "Explorer l'Eau Industrielle",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Installation de filtration d'eau industrielle en acier inoxydable",
          },
        },
        {
          title: "Filtration et Tamisage de Liquides",
          text: "Paniers filtrants, cylindres et toiles hollandaises pour la clarification de fluides et la rétention de suspensions.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Application Associée",
          linkLabel: "Explorer le Tamisage Liquide",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Toile métallique en acier inoxydable pour filtration de liquides",
          },
        },
        {
          title: "Grilles à Fil Profilé",
          text: "Panneaux plans, cribles courbes et tubes cylindriques à fil profilé pour la séparation solide-liquide intensive.",
          to: "/WedgeWireScreenPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer le Fil Profilé",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Tube filtrant à fil profilé en inox pour eaux usées et eau",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Tubes soudés, cylindres frittés et paniers métalliques adaptés aux carters de filtres autonettoyants.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubes et cylindres filtrants en acier inoxydable soudés et frittés",
          },
        },
      ],
    },
    cta: {
      id: "wastewater-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Grilles et Tamis d'Eaux Usées",
      lead: "Indiquez votre seuil de coupure, les dimensions hors tout, la nuance d'acier et les interfaces de montage pour une proposition adaptée.",
      secondaryLabel: "Retour à la Filtration d'Eau",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "WastewaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtração de Água e Líquidos",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Telas e Peneiras de Aço Inoxidável para Filtração de Efluentes",
      lead: "Telas de arame tecidas de alta resistência, meios sinterizados e telas de arame em cunha com ranhura contínua projetadas para desaguamento de lodo, retenção primária e tratamento de efluentes.",
      secondaryCta: {
        label: "Filtração de Água e Líquidos",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Equipamento industrial de desaguamento e peneiramento rotativo com tela filtrante de aço inoxidável",
      },
      cardNote: "Construção totalmente metálica e perfis antientupimento",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de Abertura", value: "20 µm – 5.000 µm" },
        { label: "Ligas Metálicas", value: "AISI 304 / 316L" },
        { label: "Construção", value: "Soldado / Sinterizado / Ranhura Contínua" },
        { label: "Limpabilidade", value: "Retrolavagem Automática / Aspersão" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Separação Sólido-Líquido Robusta para Efluentes de Alta Turbidez",
      paragraphs: [
        "Estações de tratamento e circuitos industriais de efluentes operam sob vazões contínuas e teores variáveis de sólidos em suspensão. Resíduos fibrosos, areias minerais e flocos biológicos precisam ser retidos de forma ininterrupta sem provocar colmatação precoce ou perdas de carga descontroladas.",
        "Para atender a esses requisitos, a MutualFilter fornece meios metálicos de separação que incluem telas de arame em cunha de ranhura contínua, telas holandesas reforçadas e laminados sinterizados multicamadas. A geometria dos perfis previne o travamento em dois pontos e garante a liberação rápida da torta em raspadores mecânicos ou lavagens por bicos de aspersão.",
        "Fabricadas em aço inoxidável passivado AISI 304 e 316L, as telas toleram compostos químicos industriais, cloretos e rotinas de lavagem CIP cáustica, mantendo a estabilidade mecânica e a precisão dimensional por longos períodos.",
      ],
      keyPoints: [
        "Perfis de arame em V e telas tecidas calibradas que evitam o entupimento e facilitam a liberação do bolo de filtração",
        "Geometrias com ampla área aberta que sustentam alta vazão hidráulica com perda de carga reduzida",
        "Aços inoxidáveis AISI 304 e 316L passivados resistentes a efluentes agressivos e limpezas químicas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
        alt: "Equipamento industrial de tratamento de efluentes com telas de aço inoxidável",
      },
    },
    specTable: {
      heading: "Configurações de Meios Filtrantes para Efluentes",
      intro: "Meios filtrantes metálicos comuns para desarenação, peneiras rotativas de tambor e prensas de desaguamento.",
      columns: [
        { key: "stage", label: "Etapa de Tratamento", align: "left" },
        { key: "media", label: "Estrutura do Meio Filtrante", align: "left" },
        { key: "rating", label: "Ranhura / Abertura Nominal", align: "right" },
        { key: "alloys", label: "Ligas Típicas", align: "right" },
        { key: "mechanism", label: "Método de Limpeza", align: "right" },
      ],
      rows: [
        {
          stage: "Gradeamento Grosso e Captação",
          media: "Painel plano / cilindro de arame cunha",
          rating: "500 µm – 5.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Raspador mecânico / grade móvel",
        },
        {
          stage: "Peneiramento Fino em Tambor Rotativo",
          media: "Tubo de criba em V / tela curva",
          rating: "100 µm – 1.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Barra de aspersão pressurizada",
        },
        {
          stage: "Desaguamento de Lodo e Prensagem",
          media: "Tela tecida de malha quadrada pesada",
          rating: "150 µm – 800 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Lavagem de esteira / raspador",
        },
        {
          stage: "Clarificação Terciária e Polimento",
          media: "Trama holandesa lisa / tubo sinterizado",
          rating: "20 µm – 100 µm",
          alloys: "AISI 316L",
          mechanism: "Pulso automático de retrolavagem",
        },
      ],
      caption: "Todas as dimensões e aberturas de ranhura são customizadas conforme as vazões do processo.",
    },
    features: {
      heading: "Destaques Construtivos e Operacionais",
      intro: "Nossas telas metálicas para efluentes reúnem resistência estrutural mecânica, geometria anti-obstrução e excelente desempenho metalúrgico.",
      items: [
        {
          title: "Geometria em V Antientupimento",
          text: "As ranhuras contínuas com abertura divergente asseguram apenas dois pontos de contato com as partículas, minimizando o risco de travamento nos poros.",
        },
        {
          title: "Reforços Estruturais Robustos",
          text: "Barras de suporte espessas e tubos internos perfurados mantêm a integridade sob transientes hidráulicos e forças de raspagem mecânica.",
        },
        {
          title: "Resistência à Corrosão Química",
          text: "Ligas AISI 304 e 316L passivadas suportam variações de pH, efluentes industriais e produtos cáusticos em lavagens CIP rotineiras.",
        },
        {
          title: "Compatibilidade com Retrolavagem",
          text: "Superfícies metálicas uniformes proporcionam retrolavagem rápida e lavagem por bicos injetores sem deslocar ou desgastar a tela.",
        },
      ],
    },
    related: {
      heading: "Aplicações e Produtos Relacionados",
      intro: "Conheça soluções integradas de filtração de água e meios metálicos de precisão fabricados pela MutualFilter.",
      cards: [
        {
          title: "Filtração de Água e Líquidos",
          text: "Soluções completas de separação metálica para captação, refrigeração e tratamento de efluentes industriais.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Visão geral de filtração de água e meios metálicos de separação",
          },
        },
        {
          title: "Filtração de Água Industrial",
          text: "Telas para circuitos de água de resfriamento, tomadas de processo e proteção de bombas com baixa perda de carga.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Água Industrial",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Equipamento industrial de filtração de água em aço inoxidável e tela de precisão",
          },
        },
        {
          title: "Filtração e Retenção de Líquidos",
          text: "Cestos metálicos reforçados, tubos e telas holandesas para clarificação de fluidos e retenção em suspensões.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Retenção de Líquidos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Malha de arame de aço inoxidável em trama holandesa para filtração de líquidos",
          },
        },
        {
          title: "Telas de Arame em Cunha",
          text: "Painéis planos, telas curvas tipo peneira parabólica e tubos cilíndricos com ranhura contínua antientupimento.",
          to: "/WedgeWireScreenPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Arame em Cunha",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Tubo de tela de arame cunha em aço inoxidável para filtração de água e efluentes",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Tubos soldados, cilindros sinterizados e cestos customizados para carcaças com retrolavagem automática.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos e cilindros filtrantes soldados e sinterizados em aço inoxidável",
          },
        },
      ],
    },
    cta: {
      id: "wastewater-quote",
      eyebrow: "Consulta de Engenharia",
      heading: "Solicite uma Cotação para Telas de Filtração de Efluentes",
      lead: "Informe a abertura de ranhura desejada, dimensões totais, liga de aço inoxidável e fixação mecânica para análise técnica e orçamento.",
      secondaryLabel: "Voltar para Filtração de Água",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
